// P7 · Perfil (documento 07). Sesión, mi cuenta (correo y cuentas vinculadas, D51), tema, versión y el detalle de lo
// que falta sincronizar.
import { Boton, Chip, cx, HojaInferior, Icono, Tarjeta, type TonoChip, useToast } from '@reparto/ui';
import { formatoHora, type TipoOperacion } from '@reparto/shared/nucleo';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { VERSION_APP } from '../config';
import type { OperacionLocal } from '../datos/db';
import { useEstado } from '../datos/ganchos';
import { soltarMovil } from '../datos/paquete';
import { borrarDatosLocales, salir } from '../datos/sesion';
import { descartar, reintentar, sincronizar, useSync } from '../datos/sync';
import { EsqueletoImpresora, EsqueletoPerfil } from '../componentes/Esqueletos';
import { Marco } from '../componentes/Marco';
import { SeccionCuenta } from '../componentes/SeccionCuenta';

// La sección de la impresora trae todo lo de imprimir (Bluetooth, plantilla): se carga aparte.
const SeccionImpresora = lazy(() => import('../componentes/SeccionImpresora'));

const TIPO: Record<TipoOperacion, string> = {
  tildar: 'Tilde de carga',
  cargar_item: 'Carga en parte',
  confirmar_carga: 'Confirmación de carga (Recibí conforme)',
  optimizar: 'Optimización de ruta',
  reordenar: 'Cambio de orden',
  salir: 'Salida a ruta',
  llegada: 'Llegada a cliente',
  entregar: 'Entrega',
  no_entregar: 'No entrega',
  cerrar: 'Cierre y rendición',
  fijar_ubicacion_cliente: 'Ubicación de cliente',
};
const ESTADO: Record<OperacionLocal['estado'], { texto: string; tono: TonoChip }> = {
  pendiente: { texto: 'Por enviar', tono: 'aviso' },
  enviando: { texto: 'Enviando', tono: 'info' },
  aplicada: { texto: 'Enviada', tono: 'exito' },
  rechazada: { texto: 'Rechazada', tono: 'peligro' },
  descartada: { texto: 'Descartada', tono: 'neutro' },
};

// Notificaciones del chat (decisión D40): viene con el código del chat, aparte de la primera carga.
const SeccionNotificaciones = lazy(() => import('../componentes/SeccionNotificaciones'));

type Tema = 'sistema' | 'claro' | 'oscuro';
function leerTema(): Tema {
  try {
    const t = localStorage.getItem('tema');
    return t === 'claro' || t === 'oscuro' ? t : 'sistema';
  } catch {
    return 'sistema';
  }
}
function aplicarTema(t: Tema) {
  try {
    if (t === 'sistema') localStorage.removeItem('tema');
    else localStorage.setItem('tema', t);
  } catch {
    /* sin almacenamiento: solo por esta vez */
  }
  const oscuro = t === 'oscuro' || (t === 'sistema' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', oscuro);
}

export function Perfil() {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const { sesion, vista, cola } = useEstado();
  const sync = useSync();
  const [tema, setTema] = useState<Tema>(leerTema);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const { hash } = useLocation();
  // El indicador de sincronización de la cabecera trae acá con #sincronizacion: se muestra esa tarjeta.
  useEffect(() => {
    if (!sesion || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash, !!sesion]);
  if (!sesion) return <EsqueletoPerfil />;

  const visibles = cola.filter((o) => o.estado !== 'aplicada' && o.estado !== 'descartada');
  const recientes = cola
    .filter((o) => o.estado === 'aplicada')
    .slice(-5)
    .reverse();
  const porEnviar = cola.filter(
    (o) => o.estado === 'pendiente' || o.estado === 'enviando' || o.estado === 'rechazada',
  ).length;
  const enRuta = vista?.ruta?.estado === 'en_curso' || vista?.ruta?.estado === 'armada';
  const volver = vista
    ? vista.ruta
      ? vista.ruta.estado === 'armada'
        ? '/ruta/armar'
        : vista.ruta.estado === 'en_curso'
          ? '/ruta'
          : '/cierre'
      : '/carga'
    : '/movil';

  async function ahora() {
    setSincronizando(true);
    try {
      const n = await sincronizar();
      mostrar(n ? `Se enviaron ${n} operaciones` : 'No había nada nuevo para enviar', { tipo: 'ok' });
    } finally {
      setSincronizando(false);
    }
  }

  async function cerrarSesion() {
    await salir();
    mostrar(porEnviar ? 'Sesión cerrada. Lo pendiente queda guardado hasta que vuelvas a entrar.' : 'Sesión cerrada');
    navegar('/acceso', { replace: true });
  }

  return (
    <Marco titulo="Perfil" volver={volver}>
      <div className="space-y-3 p-4">
        <Tarjeta className="flex items-center gap-3">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-primary-700 text-xl font-medium text-white">
            {sesion.usuario.nombre.slice(0, 1)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-lg font-medium">{sesion.usuario.nombre}</div>
            <div className="text-sm text-stone-500">{sesion.usuario.telefono}</div>
            <div className="truncate text-xs text-stone-500">
              {sesion.empresa.nombre || 'Empresa'}
              {vista ? ` · ${vista.movil.nombre}` : ''}
            </div>
          </div>
        </Tarjeta>

        <Tarjeta className="scroll-mt-4 space-y-3" id="sincronizacion">
          <div className="flex items-center justify-between">
            <h2 className="font-medium">Sincronización</h2>
            <Chip
              tono={
                sync.estado === 'ok'
                  ? 'exito'
                  : sync.estado === 'error'
                    ? 'peligro'
                    : sync.estado === 'sin_red'
                      ? 'neutro'
                      : 'aviso'
              }
            >
              {sync.estado === 'ok'
                ? 'Al día'
                : sync.estado === 'error'
                  ? 'Con errores'
                  : sync.estado === 'sin_red'
                    ? 'Sin señal'
                    : sync.estado === 'enviando'
                      ? 'Enviando'
                      : `${porEnviar} por enviar`}
            </Chip>
          </div>
          <p className="text-xs text-stone-500">
            {sync.ultimaSync
              ? `Última vez: ${formatoHora(sync.ultimaSync)}`
              : 'Todavía no se sincronizó en esta sesión.'}
            {sync.ultimoError ? (
              <span className="block text-rose-700 dark:text-rose-300">{sync.ultimoError}</span>
            ) : null}
          </p>
          {visibles.length ? (
            <ul className="divide-y divide-stone-100 rounded-2xl border border-stone-100 dark:divide-stone-800 dark:border-stone-800">
              {visibles.map((o) => (
                <li key={o.operacion_id} className="px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span>{TIPO[o.tipo]}</span>
                    <Chip tono={ESTADO[o.estado].tono}>{ESTADO[o.estado].texto}</Chip>
                  </div>
                  <div className="text-xs text-stone-500">
                    {formatoHora(o.hora_dispositivo)}
                    {o.intentos ? ` · ${o.intentos} intentos` : ''}
                  </div>
                  {o.estado === 'rechazada' ? (
                    <div className="mt-1 space-y-2">
                      <p className="text-xs text-rose-700 dark:text-rose-300">{o.detalle ?? o.codigo}</p>
                      <div className="flex gap-2">
                        <Boton
                          tamano="sm"
                          variante="secundario"
                          icono="refresh"
                          onClick={() => void reintentar(o.operacion_id)}
                        >
                          Reintentar
                        </Boton>
                        <Boton
                          tamano="sm"
                          variante="peligro"
                          icono="delete"
                          onClick={() =>
                            void descartar(o.operacion_id).then((n) => mostrar(`Se descartaron ${n} operaciones`))
                          }
                        >
                          Descartar
                        </Boton>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-stone-600 dark:text-stone-300">Todo lo que hiciste ya llegó al depósito.</p>
          )}
          {recientes.length ? (
            <details className="text-xs text-stone-500">
              <summary className="cursor-pointer">Últimas enviadas</summary>
              <ul className="mt-1 space-y-0.5">
                {recientes.map((o) => (
                  <li key={o.operacion_id}>
                    {formatoHora(o.hora_dispositivo)} · {TIPO[o.tipo]}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <Boton bloque variante="secundario" icono="sync" cargando={sincronizando} onClick={() => void ahora()}>
            Reintentar ahora
          </Boton>
        </Tarjeta>

        <Suspense fallback={<EsqueletoImpresora />}>
          <SeccionImpresora empresa={sesion.empresa.nombre} />
        </Suspense>

        <Tarjeta className="space-y-2">
          <h2 className="font-medium">Tema</h2>
          <div
            className="grid grid-cols-3 rounded-full bg-stone-100 p-1 dark:bg-stone-800"
            role="radiogroup"
            aria-label="Tema"
          >
            {(['sistema', 'claro', 'oscuro'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={tema === t}
                onClick={() => (setTema(t), aplicarTema(t))}
                className={cx(
                  'flex h-10 items-center justify-center gap-1 rounded-full text-sm',
                  tema === t && 'bg-white font-medium shadow dark:bg-stone-700',
                )}
              >
                <Icono
                  nombre={t === 'sistema' ? 'brightness_auto' : t === 'claro' ? 'light_mode' : 'dark_mode'}
                  tamano={18}
                />
                {t === 'sistema' ? 'Auto' : t === 'claro' ? 'Claro' : 'Oscuro'}
              </button>
            ))}
          </div>
        </Tarjeta>

        <Suspense fallback={null}>
          <SeccionNotificaciones />
        </Suspense>

        <SeccionCuenta correo={sesion.usuario.email} />

        <div className="space-y-2">
          <Boton bloque variante="secundario" icono="help" onClick={() => navegar('/uso')}>
            Cómo se usa la app
          </Boton>
          {vista && !enRuta ? (
            <Boton
              bloque
              variante="secundario"
              icono="swap_horiz"
              onClick={() => void soltarMovil().then(() => navegar('/movil', { replace: true }))}
            >
              Cambiar de móvil
            </Boton>
          ) : null}
          <Boton bloque variante="secundario" icono="logout" onClick={() => void cerrarSesion()}>
            Cerrar sesión
          </Boton>
          <Boton
            bloque
            variante="peligro"
            icono="delete_forever"
            disabled={porEnviar > 0}
            onClick={() => setConfirmarBorrar(true)}
          >
            Borrar datos locales
          </Boton>
          {porEnviar > 0 ? (
            <p className="text-center text-xs text-stone-500">
              No se pueden borrar los datos mientras haya operaciones por enviar.
            </p>
          ) : null}
        </div>

        <p className="pt-2 text-center text-xs text-stone-400">
          Reparto · versión {VERSION_APP}
          {sesion.dispositivo_id ? ` · disp. ${sesion.dispositivo_id.slice(-6)}` : ''}
        </p>
      </div>

      <HojaInferior
        abierta={confirmarBorrar}
        onCerrar={() => setConfirmarBorrar(false)}
        titulo="¿Borrar los datos de este celular?"
      >
        <p className="mb-4 text-sm text-stone-600 dark:text-stone-300">
          Se borra el día descargado y la sesión. Lo que ya está en el depósito no se toca. Vas a tener que ingresar de
          nuevo.
        </p>
        <Boton
          bloque
          tamano="lg"
          variante="peligro"
          icono="delete_forever"
          onClick={() =>
            void borrarDatosLocales().then(() => {
              setConfirmarBorrar(false);
              navegar('/acceso', { replace: true });
            })
          }
        >
          Sí, borrar
        </Boton>
      </HojaInferior>
    </Marco>
  );
}
